import React from 'react'
import {
  Box,
  Group,
  Select,
  Button,
  Divider,
  Stack,
  Switch,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { WidgetContextProvider } from '../providers'
import { SortableWidgetChildren } from '../SortableComponents'
import { ContainerWrapper } from '../ContainerWrapper'
import { z } from 'zod'
import { useWidgetEditor } from '../state'
import { COMPONENTS, STATISTIC } from '../schemas'
import { useMetrics } from '../api'
import cls from './common.module.css'
import { MetricFilterContainerOptions } from '../schemas'

type OptionsType = z.infer<typeof MetricFilterContainerOptions>

export default function MetricFilterContainer({ id }: { id: string }) {
  const [selectedMetric, setSelectedMetric] = React.useState<string | null>(
    null
  )
  const [selectedComponent, setSelectedComponent] = React.useState<
    string | null
  >('actual')
  const [selectedStatistic, setSelectedStatistic] = React.useState<
    string | null
  >('value')

  const editor = useWidgetEditor()
  const config = editor.useConfig(id)
  const { data: metrics } = useMetrics()

  if (config.widget !== 'MetricFilterContainer')
    throw new Error('Invalid widget type, expected MetricFilterContainer')

  const options: OptionsType = config.options

  // Prepare metrics for select
  const metricOptions = metrics.map((metric) => ({
    value: metric.key,
    label: metric.name,
  }))

  return (
    <ContainerWrapper
      name="Metric Filter"
      id={id}
      onCancel={editor.endEditWidget}
      onSave={editor.save}
    >
      <Box
        style={{
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          padding: '16px',
          backgroundColor: '#eee',
        }}
      >
        <Group gap="xs" mb="md" align="flex-end">
          {options.showMetricFilter && (
            <Select
              label="Metric"
              value={selectedMetric}
              onChange={setSelectedMetric}
              data={metricOptions}
              placeholder="Select a metric"
              searchable
              clearable
              style={{ minWidth: '200px' }}
            />
          )}

          {options.showComponentFilter && (
            <Select
              label="Component"
              value={selectedComponent}
              onChange={setSelectedComponent}
              data={COMPONENTS}
              placeholder="Select component"
              style={{ minWidth: '150px' }}
            />
          )}

          {options.showStatisticFilter && (
            <Select
              label="Statistic"
              value={selectedStatistic}
              onChange={setSelectedStatistic}
              data={STATISTIC}
              placeholder="Select statistic"
              style={{ minWidth: '150px' }}
            />
          )}
        </Group>

        <WidgetContextProvider
          context={{
            metric: selectedMetric || undefined,
            component: selectedComponent || undefined,
            statistic: selectedStatistic || undefined,
          }}
        >
          <Group gap={8} className={cls.flexFill}>
            <SortableWidgetChildren id={id} dir="horizontal" />
          </Group>
        </WidgetContextProvider>
      </Box>
    </ContainerWrapper>
  )
}

export const MetricFilterContainerRegistry = {
  component: MetricFilterContainer,
  formComponent: MetricFilterContainerForm,
  mock: () => null,
  getInitialOptions: () => MetricFilterContainerOptions.parse({}),
}

// Form component for editing options
export function MetricFilterContainerForm({ id }: { id: string }) {
  const editor = useWidgetEditor()
  const isSaving = editor.useIsSaving()
  const config = editor.useConfig(id)
  const form = useForm({
    mode: 'uncontrolled',
    initialValues: structuredClone(config.options),
  })

  const onSubmit = React.useCallback(
    (values: unknown) => {
      editor.setOptions(values)
      editor.save()
    },
    [editor]
  )

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="sm">
        <Divider label="Filter Options" />
        <Switch
          {...form.getInputProps('showMetricFilter', { type: 'checkbox' })}
          label="Show Metric Filter"
        />
        <Switch
          {...form.getInputProps('showComponentFilter', { type: 'checkbox' })}
          label="Show Component Filter"
        />
        <Switch
          {...form.getInputProps('showStatisticFilter', { type: 'checkbox' })}
          label="Show Statistic Filter"
        />
      </Stack>

      <Group justify="flex-end" mt="md">
        <Button color="red.4" variant="outline" onClick={editor.endEditWidget}>
          Cancel
        </Button>
        <Button type="submit" loading={isSaving}>
          Save
        </Button>
      </Group>
    </form>
  )
}
